import { useEffect } from 'react'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import LoadingSpinner from '../components/LoadingSpinner.jsx'

/**
 * EmailLinkWrapper - Optional wrapper component for email landing pages
 * 
 * Purpose: Provides common functionality for all email landing pages:
 * - Authentication check
 * - Loading states
 * - Error boundaries
 * - Common styling
 * 
 * Usage: Wrap email landing page content with this component for consistent UX
 */
function EmailLinkWrapper({ 
  children, 
  requireAuth = true, 
  title = 'Loading...',
  subtitle = 'Please wait',
}) {
  const { user, loading: authLoading } = useCloudContext()

  // Analytics tracking for email link usage
  useEffect(() => {
    if (user && !authLoading) {
      // Track email link landing (optional analytics)
      const urlParams = new URLSearchParams(window.location.search)
      const emailType = urlParams.get('emailType')
      const emailId = urlParams.get('emailId')
      
      if (emailType || emailId) {
        // You can add analytics tracking here
        // console.log('Email link accessed:', { emailType, emailId, userId: user.uid })
      }
    }
  }, [user, authLoading])

  // Show loading while authentication is being determined
  if (authLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <div className="mt-3">
            <h5 className="mb-1">{title}</h5>
            <p className="text-muted">{subtitle}</p>
          </div>
        </div>
      </div>
    )
  }

  // Check authentication requirement
  if (requireAuth && !user) {
    // Store the current URL for redirect after login
    const currentUrl = window.location.pathname + window.location.search
    const loginUrl = `/login?returnUrl=${encodeURIComponent(currentUrl)}`
    
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-lg border-0">
                <div className="card-body p-4 text-center">
                  <div className="mb-3">
                    <i className="bi bi-person-lock text-warning" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="mb-3">Authentication Required</h4>
                  <p className="text-muted mb-4">
                    You need to be signed in to access this page. Please sign in to continue.
                  </p>
                  <div className="d-grid gap-2">
                    <button 
                      className="btn btn-primary"
                      onClick={() => window.location.href = loginUrl}
                    >
                      <i className="bi bi-box-arrow-in-right me-2"></i>
                      Sign In
                    </button>
                  </div>
                  <div className="text-center mt-3">
                    <small className="text-muted">
                      Make sure you&apos;re using the same browser and account that was invited.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Render children with email link wrapper styling
  return (
    <div className="email-link-page">
      {children}
    </div>
  )
}

export default EmailLinkWrapper
